import React, { useState } from 'react';
import type { NodeType, RelationshipType } from '../types';
import {
  getPairingSourceId,
  getPairingTargetId,
  getRelationshipNameRuleKind,
  getRelationshipPrefix,
  relationshipToPairings,
} from '../utils/relationshipRules';

interface TypeInfoProps {
  type: NodeType;
  relationshipRules: RelationshipType[];
  onOpenBFOLegend?: () => void;
}

interface RuleEntry {
  name: string;
  otherType: string;
  multiplicity: string;
}

const formatRelationshipRuleLabel = (ruleName: string): string => {
  const kind = getRelationshipNameRuleKind(ruleName);
  if (kind === 'any') {
    return 'Any Relationship';
  }
  if (kind === 'prefix') {
    const prefix = getRelationshipPrefix(ruleName);
    return `Any ${prefix}* Relationship`;
  }
  return ruleName;
};

/**
 * TypeInfo component - Displays node type definition and relationship rules
 */
const TypeInfo: React.FC<TypeInfoProps> = ({ type, relationshipRules, onOpenBFOLegend }) => {
  const [expanded, setExpanded] = useState(false);
  if (!type) return null;

  const propertyDescriptions = Object.entries(
    ((type.properties_schema as { properties?: Record<string, unknown> } | undefined)?.properties) ?? {}
  )
    .map(([propertyName, propertyValue]) => {
      const description = typeof propertyValue === 'object' && propertyValue !== null
        ? (propertyValue as { description?: unknown }).description
        : undefined;
      return {
        propertyName,
        description: typeof description === 'string' ? description.trim() : '',
      };
    })
    .filter((entry) => entry.description.length > 0);

  // Flatten all allowed_pairings for all relationshipRules
  const fromRules: RuleEntry[] = [];
  const toRules: RuleEntry[] = [];

  if (relationshipRules && relationshipRules.length > 0) {
    relationshipRules.forEach((rel) => {
      relationshipToPairings(rel).forEach((pair) => {
        const sourceType = getPairingSourceId(pair);
        const targetType = getPairingTargetId(pair);

        if (sourceType === type.type) {
          fromRules.push({
            name: rel.name,
            otherType: targetType,
            multiplicity: pair.multiplicity || rel.source_cardinality || '',
          });
        }
        if (targetType === type.type) {
          toRules.push({
            name: rel.name,
            otherType: sourceType,
            multiplicity: pair.multiplicity || rel.source_cardinality || '',
          });
        }
      });
    });
  }

  return (
    <div className="type-info">
      <button
        type="button"
        className="type-chip"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="type-chip-value">{type.name}</span>
        <span className="type-chip-caret" aria-hidden="true">{expanded ? '\u25be' : '\u25b8'}</span>
      </button>
      {expanded && (
        <>
          <p>{type.description}</p>
          {(type.bfo_class || type.bfo_explanation) && (
            <div className="type-info__bfo">
              <div className="type-info__bfo-header">
              </div>
              {type.bfo_class && <p><strong>BFO Class:</strong> {type.bfo_class}</p>}
              {type.bfo_explanation && <p>{type.bfo_explanation}</p>}
              {onOpenBFOLegend && (
                  <button type="button" className="type-info__bfo-link" onClick={onOpenBFOLegend}>
                    BFO Overview
                  </button>
                )}
            </div>
          )}
          {propertyDescriptions.length > 0 && (
            <div className="relationship-rules" style={{ marginBottom: 12 }}>
              <p><strong>Node-specific properties</strong></p>
              <ul>
                {propertyDescriptions.map((entry) => (
                  <li key={entry.propertyName}>
                    <strong>{entry.propertyName}</strong>: {entry.description}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="relationship-rules">
            <ul>
              {fromRules.map((rule, idx) => (
                <li key={'from-' + idx}>
                  <strong>{formatRelationshipRuleLabel(rule.name)}</strong> to <strong>{rule.otherType}</strong> ({rule.multiplicity})
                </li>
              ))}
              {toRules.map((rule, idx) => (
                <li key={'to-' + idx}>
                  <strong>{formatRelationshipRuleLabel(rule.name)}</strong> from <strong>{rule.otherType}</strong> ({rule.multiplicity})
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default TypeInfo;
